<template>
	<Dialog
		:dialogVisible="showSupportDialog"
		:title="'Contact Support'"
		:message="'Our LiveSwitch Customer Success Managers are here to help. Submit your question or feedback below, and please include the best phone number at which to reach you. You can also reach us through the email and phone number below.'"
		:agreeText="sendingSupportRequest ? 'Sending' : 'Send'"
		:cancelText="'Back'"
		:disableAgreeButton="sendingSupportRequest"
		@agree="submitSupportRequest"
		@cancel="close">
		<template v-slot:dialog-body>
			<div class="mt-4">
				<div>
					Email:
					<a href="mailto:help@liveswitch.com" target="_blank" class="text-decoration-none"
						>help@liveswitch.com</a
					>
				</div>
				<div>
					Phone:
					<a href="tel:+1 844-587-9824" target="_blank" class="text-decoration-none">+1 844-587-9824</a>
				</div>
				<v-form ref="form">
					<v-textarea
						id="support-dialog-message"
						class="mt-4"
						outlined
						rows="5"
						v-model.trim="supportDialogContent"
						:disabled="sendingSupportRequest"
						label="Message"
						:maxLength="2000"
						:rules="messageRules"
						hide-details="auto"
						validate-on-blur
						v-test="'contact-support-message'" />
				</v-form>
			</div>
		</template>
	</Dialog>
</template>
<script lang="ts" setup>
	import { ref, defineExpose, onMounted, getCurrentInstance } from "vue";
	import Dialog from "./Dialog.vue";
	import api from "../api";
	import * as Sentry from "@sentry/vue";
	import { getMessageFromError, calculateTimeout } from "@/helpers/utilities";

	const showSupportDialog = ref(false);
	const supportDialogContent = ref("");
	const sendingSupportRequest = ref(false);
	const me = ref(null as any);
	const instance = ref(null as any);
	const form = ref<any>(null);
	const messageRules = [
		(v: string) => !!v || "Message is required.",
		(v: string) => v.length <= 2000 || "Message must be less than 2000 characters.",
	];

	defineExpose({
		open,
		submitSupportRequest,
	});

	onMounted(async () => {
		try {
			instance.value = getCurrentInstance();
			me.value = await api.getMe();
		} catch (e) {
			console.warn("error", e);
		}
	});

	function open() {
		showSupportDialog.value = true;
		supportDialogContent.value = "";
		reset();
	}

	async function submitSupportRequest() {
		try {
			const isFormValid = await form.value?.validate();
			if (!isFormValid) {
				return;
			}

			sendingSupportRequest.value = true;
			const eventId = Sentry.captureMessage("User Feedback");
			const sentryUserFeedback = {
				event_id: eventId,
				email: me.value?.user.email || "",
				name: me.value?.user.name || "",
				comments: supportDialogContent.value,
			};
			Sentry.captureUserFeedback(sentryUserFeedback);
			await api.submitSupportRequest(supportDialogContent.value);
			const toastSuccessMessage =
				"Your support request has been submitted, and someone from our team will get back to you right away.";
			await instance.value?.proxy.$toast.success(toastSuccessMessage, { timeout: calculateTimeout(toastSuccessMessage) });
			close();
		} catch (e: any) {
			const errorMessage = getMessageFromError(e);
			instance.value?.proxy.$toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
			Sentry.captureException(e, {
				tags: {
					method: "submitSupportRequest",
					file: "ContactSupportDialog",
				},
			});
		} finally {
			sendingSupportRequest.value = false;
		}
	}

	function close() {
		showSupportDialog.value = false;
		reset();
	}

	function reset() {
		supportDialogContent.value = "";
		sendingSupportRequest.value = false;
		if (form.value) {
			form.value.resetValidation();
		}
	}
</script>
